import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {isAuthRedirectError} from '../../../shared/services/auth.service';
import {logError} from '../../../shared/services/logger.service';
import {getCompany} from '../../../shared/services/company.service';
import {ExternalCompanyDto} from '../../../shared/dtos/company.dto';
import {getLocationFromAddress} from '../../../shared/misc/location-helpers';
import {usePermissions} from '../../../shared/permissions/PermissionContext';
import {PermissionCode} from '../../../shared/permissions/permissions';
import {
  ActionBar,
  ButtonPair,
  CompanyBranding,
  HeaderButton,
  RecordDates,
  RecordMessage,
  RecordPage,
  RedButton,
  TitleRow,
  WideButtonRow,
} from '../../../shared/components/record/RecordKit';
import {createClient, getClients} from '../clients.service';
import {getAllClientContacts} from '../client-contacts.service';
import {ClientContactDto} from '../dtos/client-contact.dto';
import {ClientDto} from '../dtos/client.dto';
import {createAppointment, updateAppointment} from '../../appointment/appointment.service';
import {AppointmentStatus} from '../../appointment/dto/appointment.dto';
import type {AppointmentPageState} from '../../appointment/record/AppointmentPage';
import {clientHasRecords, deleteClientRecord, getClientRecordDates, RequestError} from './client-profile.service';
import {
  addressLine,
  ClientFields,
  ContactEntry,
  ContactKind,
  CreateQuestion,
  hasChoices,
  isMainKey,
  pickedEntry,
  Picks,
  promptNote,
  promptReady,
  RecordKind,
} from './contact-entries';
import {ClientCard, ClientEditCard} from './ClientCard';
import {ClientSearchField} from './ClientSearchField';
import {ClientHistory, ClientHistorySection, HistoryCategory, loadClientHistory} from './ClientHistory';
import {ADDRESS_NOT_FOUND, ClientDraft, NAME_REQUIRED, useClientEditing} from './useClientEditing';

/*
 * The client's own page (SQ-575): Android's client record
 * (clients/ClientDetailScreen.kt with isClientRecord, and ClientContent.kt),
 * in the record layout every page shares (RecordKit, SQ-574). Android is the
 * spec for what it does; what a wider screen adds is in the kit and in
 * ClientProfile.css.
 *
 * View mode is the saved card. Edit turns the card into the edit card and the
 * green bar's button into Save, which writes the client's own fields and
 * brings Additional Client Info in line with its entries. As on Android there
 * is no discard: leaving the page while editing saves what Save would have.
 * /clients/new is the same page before the client exists: the edit card, and
 * Save Client under it.
 *
 * Add New Appointment (SQ-666, Android's addingAppointment) is the new page
 * too, headed "New Appointment": a search for an existing client above the
 * card, whose pick fills the card (Additional Client Info too) and makes the
 * page that client's, or a new client typed in. There is no client-only
 * save: the create buttons save the client first and carry on.
 */

/** What the page keeps in its history entry, so Back from a record finds it as it was left. */
interface PageState {
  fromList?: boolean;
  historyOpen?: HistoryCategory | null;
  /** A new client was saved by a create button: carry on with that button once the client's page is up. */
  createAfter?: RecordKind;
}

export interface ClientProfilePageProps {
  /** Undefined: a new client. */
  clientId?: number;
  /** A new client page opened by Add New Appointment (SQ-666). */
  addingAppointment?: boolean;
  /** Back to the list. */
  onClose: () => void;
  /** The client was created, saved or deleted: the list behind the page is out of date. */
  onChanged: () => void;
}

const ClientProfilePage = ({clientId, addingAppointment, onClose, onChanged}: ClientProfilePageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageState = (location.state ?? {}) as PageState;
  const {hasPermission, hasAnyPermission} = usePermissions();
  const adding = !!addingAppointment && clientId === undefined;
  // Add New Appointment: the existing client picked from the search, or the one its create button just saved.
  const [linkedId, setLinkedId] = useState<number>();
  const cardClientId = clientId ?? linkedId;
  const isNew = cardClientId === undefined;
  const [searchClients, setSearchClients] = useState<ClientDto[]>([]);
  const [searchContacts, setSearchContacts] = useState<ClientContactDto[]>([]);
  // A new client saved by Add New Appointment's create button: that button carries on once the client is loaded.
  const [pendingCreate, setPendingCreate] = useState<RecordKind>();

  const [dates, setDates] = useState<RecordDates>();
  const [company, setCompany] = useState<ExternalCompanyDto>();
  const [history, setHistory] = useState<ClientHistory>({});

  const [error, setError] = useState<string>();
  const [editing, setEditing] = useState(isNew);
  const [working, setWorking] = useState(false);
  // Open where the client's entries are worked on, which the profile is.
  const [additionalOpen, setAdditionalOpen] = useState(true);
  const [prompt, setPrompt] = useState<RecordKind | null>(null);
  const [picks, setPicks] = useState<Picks>({});
  const [dialog, setDialog] = useState<'confirmDelete' | 'deleteBlocked' | null>(null);

  const {
    client, fields, entries, contactsRead, rating, loadError, draft, settled,
    locate, commitEntries, saveAll, changeEntry: editEntry, pickAddress, changeTitle, addEntry: addEmptyEntry, tapStar, saveReview,
  } = useClientEditing(cardClientId, {editing, onError: setError, onChanged});
  const editingNow = useRef(editing);
  editingNow.current = editing;

  // ---- Load -----------------------------------------------------------------------

  useEffect(() => {
    let active = true;
    // The branding is furniture: a failure leaves it out.
    getCompany(navigate).then(c => active && setCompany(c)).catch(() => undefined);
    if (clientId === undefined) return () => { active = false; };

    const canView = (category: HistoryCategory) => {
      switch (category) {
        case 'appointments': return hasPermission(PermissionCode.AppointmentsViewList);
        case 'estimates': return hasPermission(PermissionCode.EstimatesViewList);
        // Either scope reaches the Jobs page; the server decides which jobs "assigned" returns.
        case 'jobs': return hasAnyPermission(PermissionCode.JobsViewAll, PermissionCode.JobsViewAssigned);
        case 'invoices': return hasPermission(PermissionCode.InvoicesViewList);
      }
    };
    getClientRecordDates(navigate, clientId).then(d => active && setDates(d.client)).catch(() => undefined);
    loadClientHistory(navigate, clientId, canView).then(h => active && setHistory(h)).catch(() => undefined);
    return () => { active = false; };
  }, [clientId, navigate, hasPermission, hasAnyPermission]);

  // Add New Appointment's search: every client, and their Additional Client Info (best effort: without it the
  // search still covers each client's own details).
  useEffect(() => {
    if (!adding) return undefined;
    let active = true;
    getClients(navigate).then(c => active && setSearchClients(c)).catch(e => {
      if (!isAuthRedirectError(e)) logError('Failed to load clients for the search', {}, e as Error);
    });
    getAllClientContacts(navigate).then(c => active && setSearchContacts(c)).catch(() => undefined);
    return () => { active = false; };
  }, [adding, navigate]);

  /** A client picked from the search: their whole card fills the page, which is still the new appointment's. */
  const linkClient = (id: number) => {
    if (working) return;
    setError(undefined);
    setPrompt(null);
    setPicks({});
    setLinkedId(id);
  };

  // ---- Save -------------------------------------------------------------------------

  const refreshAfterSave = (id: number) => {
    onChanged();
    getClientRecordDates(navigate, id).then(d => setDates(d.client)).catch(() => undefined);
  };

  const save = async () => {
    if (working) return;
    setWorking(true);
    setError(undefined);
    const saved = await saveAll();
    setWorking(false);
    if (!saved?.client) return;
    setEditing(false);
    refreshAfterSave(saved.client.id);
  };

  /** Creates the client (the new page only). Null: refused, and the message is up. */
  const createNew = async (): Promise<ClientDto | null> => {
    const now = draft.current;
    if (!now.fields.name.trim()) {
      setError(NAME_REQUIRED);
      return null;
    }
    let point: ClientFields;
    try {
      point = await locate(now.fields);
    } catch {
      setError(ADDRESS_NOT_FOUND);
      return null;
    }
    try {
      const created = await createClient(navigate, {
        name: now.fields.name.trim(),
        lat: point.lat,
        lng: point.lng,
        address: now.fields.address.trim(),
        city: now.fields.city.trim(),
        state: now.fields.state.trim(),
        postalCode: now.fields.postalCode.trim(),
        email: now.fields.email.trim() || undefined,
        phoneNumber: now.fields.phoneNumber.trim() || undefined,
      });
      // Entries added before the first save had no client to belong to; they do now.
      await commitEntries(created.id, {...now, contacts: []})
        .catch(e => logError('Failed to save Additional Client Info', {clientId: created.id}, e as Error));
      onChanged();
      return created;
    } catch (e) {
      if (!isAuthRedirectError(e)) setError('Failed to create client');
      return null;
    }
  };

  /** The new client's own page takes this one's place, so Back still leads to the list. */
  const openCreated = (created: ClientDto, createAfter?: RecordKind) => {
    settled.current = true;
    navigate(`/clients/${created.id}`, {replace: true, state: {fromList: pageState.fromList, createAfter}});
  };

  const saveNewClient = async () => {
    if (working) return;
    setWorking(true);
    setError(undefined);
    const created = await createNew();
    setWorking(false);
    if (created) openCreated(created);
  };

  // ---- The card's edits -----------------------------------------------------------------

  // Typing clears the last refusal.
  const changeEntry: typeof editEntry = (key, update) => {
    setError(undefined);
    editEntry(key, update);
  };

  const addEntry = (kind: ContactKind) => {
    addEmptyEntry(kind);
    setAdditionalOpen(true);
  };

  // ---- Delete -----------------------------------------------------------------------------------

  /** Delete is always pressable; whether it can happen is answered by the dialog it opens. */
  const askDelete = async () => {
    if (!client || working) return;
    // A lookup that fails counts as attached: the server would refuse anyway, and a reason beats an error.
    const attached = await clientHasRecords(navigate, client.id).catch(() => true);
    setDialog(attached ? 'deleteBlocked' : 'confirmDelete');
  };

  const deleteNow = async () => {
    if (!client) return;
    setDialog(null);
    setWorking(true);
    setError(undefined);
    try {
      await deleteClientRecord(navigate, client.id);
      settled.current = true;
      onChanged();
      onClose();
    } catch (e) {
      setWorking(false);
      if (isAuthRedirectError(e)) return;
      setError(e instanceof RequestError && e.status === 400
        ? "This client has appointments or estimates and can't be deleted"
        : 'Failed to delete client');
    }
  };

  // ---- Create Appointment / Start an Estimate (SQ-426) ---------------------------------------------

  const question: CreateQuestion = {client: fields, entries, prompt, picks};

  /**
   * Creates the record for the entries picked (the ticks, else the client's
   * own) and opens it. The address goes onto the record as its own: that is
   * where the crew goes. An estimate always hangs off an appointment, so
   * starting one books an appointment for now and moves it straight on.
   */
  const createRecord = async (kind: RecordKind, saved: ClientDraft) => {
    const q: CreateQuestion = {client: saved.fields, entries: saved.entries, prompt: kind, picks};
    const address = pickedEntry(q, 'address');
    // The client's own address has its point; another is looked up, and falls back to the client's.
    const point = isMainKey(address.key) ? null : await getLocationFromAddress(addressLine(address)).catch(() => null);
    const appointment = await createAppointment(navigate, {
      clientId: saved.client!.id,
      time: kind === 'estimate' ? new Date() : undefined,
      estimateAddress: address.value,
      estimateCity: address.city,
      estimateState: address.state,
      estimatePostalCode: address.postalCode,
      estimateLat: point?.lat ?? saved.fields.lat,
      estimateLng: point?.lng ?? saved.fields.lng,
      contactNameId: pickedEntry(q, 'name').rowId ?? undefined,
      contactPhoneId: pickedEntry(q, 'phone').rowId ?? undefined,
      contactAddressId: address.rowId ?? undefined,
      contactEmailId: pickedEntry(q, 'email').rowId ?? undefined,
    });
    onChanged();
    settled.current = true;
    if (kind === 'appointment') {
      // It opens ready to edit (SQ-666). From Add New Appointment it takes this page's place, so Back is the list.
      const state: AppointmentPageState & PageState = adding ? {justCreated: true, fromList: pageState.fromList} : {justCreated: true};
      navigate(`/appointments/${appointment.id}`, {replace: adding, state});
      return;
    }
    // The appointment has become the estimate, so it is finished; the server starts the estimate with it.
    await updateAppointment(navigate, appointment.id, {status: AppointmentStatus.Completed}).catch(() => undefined);
    navigate(`/estimates/${appointment.estimateId}`, {replace: adding});
  };

  /**
   * One press does the thing, unless the client has something to choose
   * between (a second address, phone, name or email): then the first press
   * asks, with a note above the buttons and checkboxes on the lists with a
   * choice, and the second press creates. A press on the other button while
   * one is asking swaps the question.
   */
  const pressCreate = async (kind: RecordKind) => {
    if (working) return;
    if (prompt !== null && prompt !== kind) {
      setPrompt(kind);
      setPicks({});
      return;
    }
    if (prompt === kind && !promptReady(question)) return;
    setWorking(true);
    setError(undefined);
    try {
      if (isNew) {
        // Saved first, so the record has a real client; its own page carries on from there,
        // or on Add New Appointment this page, once the client is loaded as the picked one would be.
        const created = await createNew();
        if (created && adding) {
          setLinkedId(created.id);
          setPendingCreate(kind);
        } else if (created) openCreated(created, kind);
        return;
      }
      // Saved first: a pick records an entry's id, so entries typed but not yet saved have to exist to be chosen.
      const saved = editingNow.current ? await saveAll() : draft.current;
      if (!saved?.client) return;
      if (prompt === null && hasChoices({client: saved.fields, entries: saved.entries})) {
        setPrompt(kind);
        setPicks({});
        // The question's checkboxes live in Additional Client Info: open it when it asks.
        setAdditionalOpen(true);
        return;
      }
      await createRecord(kind, saved);
    } catch (e) {
      if (!isAuthRedirectError(e)) {
        logError('Failed to create the record', {clientId, kind}, e as Error);
        setError(kind === 'appointment' ? 'Failed to create the appointment' : 'Failed to start the estimate');
      }
    } finally {
      setWorking(false);
    }
  };

  // A new client saved by a create button: carry on with that button, once.
  const createAfter = pageState.createAfter ?? pendingCreate;
  const carriedOn = useRef(false);
  useEffect(() => {
    if (!createAfter || !client || !contactsRead || carriedOn.current) return;
    carriedOn.current = true;
    if (pendingCreate) setPendingCreate(undefined);
    else navigate(location.pathname, {replace: true, state: {...pageState, createAfter: undefined}});
    void pressCreate(createAfter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAfter, client?.id, contactsRead]);

  // ---- History -------------------------------------------------------------------------------------

  // One kind open at a time, kept with the page's history entry: opening a card and coming back finds it still open.
  const toggleHistory = (category: HistoryCategory) =>
    navigate(location.pathname, {replace: true, state: {...pageState, historyOpen: pageState.historyOpen === category ? null : category}});

  // ---- The page --------------------------------------------------------------------------------------

  const canEdit = hasPermission(PermissionCode.ClientsEdit);
  const canCreateAppointment = hasPermission(PermissionCode.AppointmentsCreate);
  const canStartEstimate = canCreateAppointment && hasPermission(PermissionCode.EstimatesCreate);
  const ready = isNew || (!!client && contactsRead);
  const note = promptNote(question);

  const card = {
    question,
    onPick: (entry: ContactEntry) => setPicks(current => ({...current, [entry.kind]: entry.key})),
    rating: isNew ? undefined : rating,
    onStar: tapStar,
    onSaveReview: saveReview,
    additionalOpen,
    onToggleAdditional: () => setAdditionalOpen(open => !open),
  };

  return (
    <RecordPage
      name="ClientPage"
      status={adding ? 'New' : isNew ? undefined : 'Active'}
      action={!adding && !isNew && client && canEdit
        ? <HeaderButton label={editing ? 'Save' : 'Edit'} disabled={working}
                        onClick={() => { if (editing) void save(); else setEditing(true); }}/>
        : undefined}
    >
      {loadError && <RecordMessage error text={loadError}/>}
      {!ready && !loadError && <CircularProgress/>}
      {ready && <>
        {client && !adding && hasPermission(PermissionCode.ClientsDelete) && (
          <ActionBar><RedButton label="Delete" disabled={working} onClick={() => void askDelete()}/></ActionBar>
        )}
        {adding ? <TitleRow title="New Appointment"/> : client && <TitleRow title={`Client #${client.id}`} dates={dates}/>}
        {company && <CompanyBranding name={company.name} logoUrl={company.logoUrl}/>}
        {adding && <ClientSearchField clients={searchClients} contacts={searchContacts} linkedName={client?.name} onPick={linkClient}/>}
        {editing
          ? <ClientEditCard {...card} onEntryChanged={changeEntry} onAddressPicked={pickAddress}
                            onTitleChanged={changeTitle} onAddEntry={addEntry}/>
          : <ClientCard {...card}/>}
        {error && <div className="cc-error" role="alert">{error}</div>}
        <div className="cc-actions">
          {working ? <CircularProgress/> : <>
            {isNew && !adding && (
              <WideButtonRow>
                <button type="button" className="cc-create-button" onClick={() => void saveNewClient()}>Save Client</button>
              </WideButtonRow>
            )}
            {(isNew || adding) && (
              <WideButtonRow>
                <button type="button" className="cc-create-button cc-create-button--outlined" onClick={onClose}>Cancel</button>
              </WideButtonRow>
            )}
            {note && <div className="cc-prompt-note">{note}</div>}
            {(canCreateAppointment || canStartEstimate) && (
              <ButtonPair>
                {canCreateAppointment && (
                  <button type="button" className="cc-create-button" onClick={() => void pressCreate('appointment')}
                          disabled={prompt === 'appointment' && !promptReady(question)}>
                    {prompt === 'appointment' ? 'Create appointment' : 'Create Appointment'}
                  </button>
                )}
                {canStartEstimate && (
                  <button type="button" className="cc-create-button" onClick={() => void pressCreate('estimate')}
                          disabled={prompt === 'estimate' && !promptReady(question)}>
                    {prompt === 'estimate' ? 'Start estimate' : 'Start an Estimate'}
                  </button>
                )}
              </ButtonPair>
            )}
          </>}
        </div>
        {client && !adding && <ClientHistorySection history={history} open={pageState.historyOpen ?? null} onToggle={toggleHistory}/>}
      </>}

      <Dialog open={dialog === 'confirmDelete'} onClose={() => setDialog(null)}>
        <DialogTitle>Delete this client?</DialogTitle>
        <DialogContent>
          <DialogContentText>Nothing is attached to this client yet, so this removes them for good.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void deleteNow()}>Delete</Button>
        </DialogActions>
      </Dialog>
      {/* No Cancel (nothing is pending) and no red (nothing is about to happen). */}
      <Dialog open={dialog === 'deleteBlocked'} onClose={() => setDialog(null)}>
        <DialogTitle>This client can't be deleted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            They have appointments, estimates, jobs or invoices attached, and deleting them would take that history with it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </RecordPage>
  );
};

export default ClientProfilePage;
